import React, { useState, useEffect } from "react";
import api from "../lib/api";


const fmt = (n: number, d = 0) =>
  n.toLocaleString("en-KE", { minimumFractionDigits: d, maximumFractionDigits: d });

/* ─── types ─────────────────────────────────────────────── */
type ReqStatus = "pending" | "approved" | "rejected" | "cancelled";

interface CreditPolicy {
  id: number;
  property: number;
  property_name: string;
  is_enabled: boolean;
  credit_price_per_unit: string;
  max_credit_amount: string;
  grace_period_days: number;
  late_fee_percent: string;
}

interface CreditAccount {
  id: number;
  tenant: number;
  tenant_name: string;
  tenant_phone: string | null;
  meter_serial: string;
  property_name: string;
  unit_number: string;
  credit_limit: string;
  outstanding_balance: string;
  available_credit: string;
  status: "active" | "suspended" | "closed";
  due_date: string | null;
}

interface CreditRequest {
  id: number;
  tenant: number;
  tenant_name: string;
  tenant_phone: string | null;
  meter_serial: string;
  property_name: string;
  unit_number: string;
  amount_requested: string;
  units_to_allocate: string | null;
  credit_price_per_unit: string;
  status: ReqStatus;
  rejection_reason: string;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  created_at: string;
}

interface CreditRepayment {
  id: number;
  credit_account: number;
  tenant_name: string;
  meter_serial: string;
  amount: string;
  method: string;
  mpesa_receipt: string;
  notes: string;
  recorded_by_name: string | null;
  created_at: string;
}

interface Property {
  id: number;
  name: string;
  token_value: string;
}

/* ─── helpers ───────────────────────────────────────────── */
function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />;
}

const STATUS_META: Record<ReqStatus, { label: string; bg: string; text: string; dot: string }> = {
  pending:   { label: "Pending",   bg: "bg-amber-50",   text: "text-amber-600",   dot: "bg-amber-400" },
  approved:  { label: "Approved",  bg: "bg-emerald-50", text: "text-emerald-600", dot: "bg-emerald-400" },
  rejected:  { label: "Rejected",  bg: "bg-red-50",     text: "text-red-500",     dot: "bg-red-400" },
  cancelled: { label: "Cancelled", bg: "bg-gray-100",   text: "text-gray-400",    dot: "bg-gray-300" },
};

function Toggle({ value, onChange, disabled = false }: { value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      onClick={() => !disabled && onChange(!value)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${value ? "bg-sky-500" : "bg-gray-200"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-md transform transition-transform ${value ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

/* ─── policy editor modal ─────────────────────────────────── */
function PolicyModal({
  policy,
  properties,
  onClose,
  onSaved,
}: {
  policy: CreditPolicy | null;
  properties: Property[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    property: policy?.property ?? properties[0]?.id ?? "",
    is_enabled: policy?.is_enabled ?? true,
    credit_price_per_unit: policy?.credit_price_per_unit ?? "1.50",
    max_credit_amount: policy?.max_credit_amount ?? "500",
    grace_period_days: policy?.grace_period_days ?? 30,
    late_fee_percent: policy?.late_fee_percent ?? "0",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const regPrice = properties.find((p) => p.id === Number(form.property))?.token_value;

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      if (policy) {
        await api.patch(`/credit/policies/${policy.id}/`, form);
      } else {
        await api.post(`/credit/policies/`, form);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      const d = e.response?.data;
      setError(typeof d === "string" ? d : Object.values(d ?? {}).flat().join(" ") || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-[#0f2557] to-[#0e2050] px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-white font-bold text-[15px]">{policy ? "Edit Credit Policy" : "New Credit Policy"}</h2>
              <p className="text-sky-300/70 text-[12px] mt-0.5">Set credit terms for a property</p>
            </div>
            <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {!policy && (
            <div>
              <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">Property</label>
              <select
                value={form.property}
                onChange={(e) => setForm({ ...form, property: Number(e.target.value) })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
              >
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}

          {/* enable toggle */}
          <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
            <div>
              <p className="text-[13px] font-semibold text-gray-800">Enable Credit Billing</p>
              <p className="text-[11.5px] text-gray-400 mt-0.5">Tenants on this property can request credit water</p>
            </div>
            <Toggle value={form.is_enabled} onChange={(v) => setForm({ ...form, is_enabled: v })} />
          </div>

          {/* credit price */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">
              Credit Price per Unit (KES)
            </label>
            <input
              type="number"
              step="0.01"
              value={form.credit_price_per_unit}
              onChange={(e) => setForm({ ...form, credit_price_per_unit: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
            />
            {regPrice && (
              <p className="text-[11.5px] text-gray-400 mt-1.5">
                Regular rate: KES {parseFloat(regPrice).toFixed(2)}/unit · Credit rate is typically higher
              </p>
            )}
          </div>

          {/* grid fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">Max Credit (KES)</label>
              <input
                type="number"
                value={form.max_credit_amount}
                onChange={(e) => setForm({ ...form, max_credit_amount: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">Grace Period (days)</label>
              <input
                type="number"
                value={form.grace_period_days}
                onChange={(e) => setForm({ ...form, grace_period_days: Number(e.target.value) })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">Late Fee (%)</label>
              <input
                type="number"
                step="0.5"
                value={form.late_fee_percent}
                onChange={(e) => setForm({ ...form, late_fee_percent: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
              />
            </div>
          </div>

          {error && (
            <p className="text-[12.5px] text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
          )}

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-semibold text-gray-600 hover:bg-gray-50 transition-all">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-sky-500 text-white text-[13px] font-semibold hover:bg-sky-600 transition-all shadow-sm disabled:opacity-60">
              {saving ? "Saving…" : "Save Policy"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── approve / reject modal ─────────────────────────────── */
function ReviewModal({
  req,
  action,
  onClose,
  onDone,
}: {
  req: CreditRequest;
  action: "approve" | "reject";
  onClose: () => void;
  onDone: () => void;
}) {
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const price = parseFloat(req.credit_price_per_unit);
  const units = parseFloat(req.amount_requested) / price;

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      if (action === "approve") {
        await api.post(`/credit/requests/${req.id}/approve/`, { notes });
      } else {
        if (!reason.trim()) { setError("Please provide a rejection reason."); setLoading(false); return; }
        await api.post(`/credit/requests/${req.id}/reject/`, { rejection_reason: reason });
      }
      onDone();
      onClose();
    } catch (e: any) {
      const d = e.response?.data;
      setError(typeof d === "string" ? d : Object.values(d ?? {}).flat().join(" ") || "Action failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className={`px-6 py-5 ${action === "approve" ? "bg-gradient-to-r from-emerald-500 to-emerald-600" : "bg-gradient-to-r from-red-500 to-red-600"}`}>
          <div className="flex items-center justify-between">
            <h2 className="text-white font-bold text-[15px]">{action === "approve" ? "Approve Credit Request" : "Reject Credit Request"}</h2>
            <button onClick={onClose} className="text-white/60 hover:text-white">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* request summary */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-[12.5px]">
            <div className="flex justify-between"><span className="text-gray-500">Tenant</span><span className="font-semibold text-gray-800">{req.tenant_name}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Property / Unit</span><span className="font-semibold text-gray-800">{req.property_name} · {req.unit_number}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Amount</span><span className="font-bold text-gray-800">KES {fmt(parseFloat(req.amount_requested))}</span></div>
            {action === "approve" && (
              <div className="flex justify-between"><span className="text-gray-500">Units to load</span><span className="font-bold text-sky-600">{fmt(units, 1)} litres</span></div>
            )}
          </div>

          {action === "approve" ? (
            <div>
              <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">Internal Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any internal notes for this approval…"
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none"
              />
              <p className="text-[11.5px] text-gray-400 mt-1.5">
                On approval, {fmt(units, 1)} litres will be loaded immediately and an SMS sent to the tenant.
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">Rejection Reason *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain why this request is being declined…"
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
              />
            </div>
          )}

          {error && <p className="text-[12.5px] text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-semibold text-gray-600 hover:bg-gray-50 transition-all">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`flex-1 py-2.5 rounded-xl text-white text-[13px] font-semibold transition-all shadow-sm disabled:opacity-60 ${action === "approve" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-red-500 hover:bg-red-600"}`}
            >
              {loading ? "Processing…" : action === "approve" ? "Approve & Load Water" : "Reject Request"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── record repayment modal ──────────────────────────────── */
function RepaymentModal({
  account,
  onClose,
  onDone,
}: {
  account: CreditAccount;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState(Math.min(parseFloat(account.outstanding_balance), 500));
  const [method, setMethod] = useState("mpesa");
  const [receipt, setReceipt] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const outstanding = parseFloat(account.outstanding_balance);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await api.post(`/credit/repayments/`, {
        credit_account: account.id, amount, method, mpesa_receipt: receipt, notes,
      });
      onDone();
      onClose();
    } catch (e: any) {
      const d = e.response?.data;
      setError(typeof d === "string" ? d : Object.values(d ?? {}).flat().join(" ") || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-[#0f2557] to-[#0e2050] px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-white font-bold text-[15px]">Record Repayment</h2>
              <p className="text-sky-300/70 text-[12px] mt-0.5">{account.tenant_name} — {account.meter_serial}</p>
            </div>
            <button onClick={onClose} className="text-white/50 hover:text-white"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/></svg></button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-[12.5px] text-amber-800">
            Outstanding balance: <strong>KES {fmt(outstanding, 2)}</strong>
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">Amount (KES)</label>
            <input type="number" value={amount} max={outstanding} onChange={(e) => setAmount(Number(e.target.value))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
            />
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-2">Payment Method</label>
            <div className="grid grid-cols-3 gap-2">
              {[["mpesa","M-Pesa"],["cash","Cash"],["bank","Bank"]].map(([val, lbl]) => (
                <button key={val} onClick={() => setMethod(val)} className={`py-2 rounded-xl border text-[12.5px] font-semibold transition-all ${method === val ? "bg-sky-500 text-white border-sky-500" : "bg-white text-gray-600 border-gray-200 hover:border-sky-300"}`}>{lbl}</button>
              ))}
            </div>
          </div>

          {method === "mpesa" && (
            <div>
              <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">M-Pesa Receipt</label>
              <input value={receipt} onChange={(e) => setReceipt(e.target.value)} placeholder="e.g. QGH7XBCDEF" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-sky-300"/>
            </div>
          )}

          <div>
            <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">Notes</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"/>
          </div>

          {error && <p className="text-[12.5px] text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-semibold text-gray-600 hover:bg-gray-50 transition-all">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-sky-500 text-white text-[13px] font-semibold hover:bg-sky-600 transition-all shadow-sm disabled:opacity-60">
              {saving ? "Saving…" : "Record Repayment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */
export default function CreditManagementPage() {
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState<CreditPolicy[]>([]);
  const [accounts, setAccounts] = useState<CreditAccount[]>([]);
  const [requests, setRequests] = useState<CreditRequest[]>([]);
  const [repayments, setRepayments] = useState<CreditRepayment[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [tab, setTab] = useState<"requests" | "accounts" | "repayments" | "policies">("requests");
  const [statusFilter, setStatusFilter] = useState<"all" | ReqStatus>("all");
  const [policyModal, setPolicyModal] = useState<{ open: boolean; policy: CreditPolicy | null }>({ open: false, policy: null });
  const [reviewModal, setReviewModal] = useState<{ req: CreditRequest; action: "approve" | "reject" } | null>(null);
  const [repaymentModal, setRepaymentModal] = useState<CreditAccount | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [polRes, accRes, reqRes, repRes, propRes] = await Promise.allSettled([
        api.get(`/credit/policies/`),
        api.get(`/credit/accounts/`),
        api.get(`/credit/requests/`),
        api.get(`/credit/repayments/`),
        api.get(`/properties/`),
      ]);
      if (polRes.status === "fulfilled") setPolicies(polRes.value.data?.results ?? polRes.value.data ?? []);
      if (accRes.status === "fulfilled") setAccounts(accRes.value.data?.results ?? accRes.value.data ?? []);
      if (reqRes.status === "fulfilled") setRequests(reqRes.value.data?.results ?? reqRes.value.data ?? []);
      if (repRes.status === "fulfilled") setRepayments(repRes.value.data?.results ?? repRes.value.data ?? []);
      if (propRes.status === "fulfilled") setProperties(propRes.value.data?.results ?? propRes.value.data ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const pendingRequests = requests.filter((r) => r.status === "pending");
  const totalOutstanding = accounts.reduce((s, a) => s + parseFloat(a.outstanding_balance), 0);
  const filteredRequests = statusFilter === "all" ? requests : requests.filter((r) => r.status === statusFilter);

  const TABS = [
    { key: "requests",   label: "Requests",   badge: pendingRequests.length > 0 ? pendingRequests.length : 0 },
    { key: "accounts",   label: "Accounts",   badge: 0 },
    { key: "repayments", label: "Repayments", badge: 0 },
    { key: "policies",   label: "Policies",   badge: 0 },
  ] as const;

  const STATUS_TABS: { key: "all" | ReqStatus; label: string }[] = [
    { key: "all", label: "All" }, { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" }, { key: "rejected", label: "Rejected" }, { key: "cancelled", label: "Cancelled" },
  ];

  return (
    <>
      <div className="space-y-7">

        {/* ── header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] font-bold text-gray-800 leading-tight">Credit Management</h1>
            <p className="text-[13px] text-gray-400 mt-0.5">Review requests, track balances, record repayments</p>
          </div>
          <button
            onClick={() => setPolicyModal({ open: true, policy: null })}
            className="flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-all"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
              <path d="M12 5v14M5 12h14" strokeLinecap="round"/>
            </svg>
            New Credit Policy
          </button>
        </div>

        {/* ── KPIs ── */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24"/>)}</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Pending Requests", value: pendingRequests.length.toString(), sub: "Awaiting your review", color: "bg-amber-50", stroke: "#f59e0b" },
              { label: "Active Accounts",  value: accounts.filter((a) => a.status === "active").length.toString(), sub: "Tenants with credit", color: "bg-sky-50", stroke: "#0ea5e9" },
              { label: "Total Outstanding", value: `KES ${fmt(totalOutstanding, 0)}`, sub: "Owed across all tenants", color: totalOutstanding > 0 ? "bg-red-50" : "bg-emerald-50", stroke: totalOutstanding > 0 ? "#ef4444" : "#10b981" },
              { label: "Policies Active",  value: policies.filter((p) => p.is_enabled).length.toString(), sub: `of ${policies.length} policies`, color: "bg-violet-50", stroke: "#6366f1" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke={s.stroke} strokeWidth={1.8} className="w-5 h-5"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20M6 15h4" strokeLinecap="round"/></svg>
                </div>
                <div>
                  <p className="text-[11.5px] text-gray-400 font-medium uppercase tracking-widest">{s.label}</p>
                  <p className="text-[20px] font-bold text-gray-800 leading-tight mt-0.5">{s.value}</p>
                  <p className="text-[11.5px] text-gray-400 mt-0.5">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── pending alert ── */}
        {pendingRequests.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-xl">⏳</span>
              <p className="text-[13px] font-semibold text-amber-800">
                {pendingRequests.length} credit request{pendingRequests.length > 1 ? "s" : ""} waiting for your review
              </p>
            </div>
            <button onClick={() => { setTab("requests"); setStatusFilter("pending"); }} className="text-[12.5px] font-bold text-amber-700 underline underline-offset-2">
              Review now →
            </button>
          </div>
        )}

        {/* ── tabs ── */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`relative flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12.5px] font-semibold transition-all ${tab === t.key ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              {t.label}
              {t.badge > 0 && (
                <span className="w-5 h-5 rounded-full bg-amber-400 text-white text-[10px] font-bold flex items-center justify-center">{t.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* ══ REQUESTS TAB ══ */}
        {tab === "requests" && (
          <div className="space-y-4">
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
              {STATUS_TABS.map((t) => (
                <button key={t.key} onClick={() => setStatusFilter(t.key)}
                  className={`px-3.5 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${statusFilter === t.key ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {t.label}
                  {t.key !== "all" && <span className="ml-1.5 text-[10.5px] bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-md">{requests.filter((r) => r.status === t.key).length}</span>}
                </button>
              ))}
            </div>

            {filteredRequests.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
                <p className="text-[14px] font-semibold text-gray-600">No requests</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        {["Ref", "Tenant", "Property / Unit", "Amount", "Units", "Status", "Date", "Actions"].map((h_) => (
                          <th key={h_} className="px-5 py-3.5 text-left text-[11px] text-gray-400 font-semibold uppercase tracking-wider whitespace-nowrap">{h_}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map((req) => {
                        const meta = STATUS_META[req.status];
                        const units = parseFloat(req.amount_requested) / parseFloat(req.credit_price_per_unit);
                        return (
                          <tr key={req.id} className="border-b border-gray-50 hover:bg-gray-50/40 transition-colors">
                            <td className="px-5 py-4 font-mono text-gray-500 text-[12px]">#CR{String(req.id).padStart(5, "0")}</td>
                            <td className="px-5 py-4">
                              <p className="font-semibold text-gray-800">{req.tenant_name}</p>
                              <p className="text-[11.5px] text-gray-400">{req.tenant_phone ?? "—"}</p>
                            </td>
                            <td className="px-5 py-4">
                              <p className="text-gray-700">{req.property_name}</p>
                              <p className="text-[11.5px] text-gray-400">Unit {req.unit_number} · {req.meter_serial}</p>
                            </td>
                            <td className="px-5 py-4 font-bold text-gray-800">KES {fmt(parseFloat(req.amount_requested))}</td>
                            <td className="px-5 py-4 text-sky-600 font-medium">{fmt(units, 1)} L</td>
                            <td className="px-5 py-4">
                              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${meta.bg} ${meta.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                                {meta.label}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-gray-400">{new Date(req.created_at).toLocaleDateString("en-KE", { dateStyle: "medium" })}</td>
                            <td className="px-5 py-4">
                              {req.status === "pending" ? (
                                <div className="flex gap-2">
                                  <button onClick={() => setReviewModal({ req, action: "approve" })} className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 text-[12px] font-semibold hover:bg-emerald-100 transition-all">Approve</button>
                                  <button onClick={() => setReviewModal({ req, action: "reject" })} className="px-3 py-1.5 rounded-lg bg-red-50 text-red-500 text-[12px] font-semibold hover:bg-red-100 transition-all">Reject</button>
                                </div>
                              ) : <span className="text-gray-300 text-[12px]">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ ACCOUNTS TAB ══ */}
        {tab === "accounts" && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {["Tenant", "Meter", "Balance Used", "Outstanding", "Due Date", "Status", "Actions"].map((h_) => (
                      <th key={h_} className="px-5 py-3.5 text-left text-[11px] text-gray-400 font-semibold uppercase tracking-wider">{h_}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((acct) => {
                    const owed = parseFloat(acct.outstanding_balance);
                    const limit = parseFloat(acct.credit_limit);
                    const pct = limit > 0 ? Math.min(100, (owed / limit) * 100) : 0;
                    return (
                      <tr key={acct.id} className="border-b border-gray-50 hover:bg-gray-50/40 transition-colors">
                        <td className="px-5 py-4">
                          <p className="font-semibold text-gray-800">{acct.tenant_name}</p>
                          <p className="text-[11.5px] text-gray-400">{acct.tenant_phone ?? "—"}</p>
                        </td>
                        <td className="px-5 py-4">
                          <p className="font-mono text-gray-700">{acct.meter_serial}</p>
                          <p className="text-[11.5px] text-gray-400">{acct.unit_number}</p>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-gray-100 rounded-full h-1.5">
                              <div className={`h-1.5 rounded-full ${pct >= 80 ? "bg-red-400" : pct >= 50 ? "bg-amber-400" : "bg-emerald-400"}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-[12px] text-gray-500">{Math.round(pct)}%</span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`font-bold ${owed > 0 ? "text-red-500" : "text-emerald-600"}`}>KES {fmt(owed, 2)}</span>
                        </td>
                        <td className="px-5 py-4 text-gray-500">{acct.due_date ? new Date(acct.due_date).toLocaleDateString("en-KE", { dateStyle: "medium" }) : "—"}</td>
                        <td className="px-5 py-4">
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${acct.status === "active" ? "bg-emerald-50 text-emerald-600" : acct.status === "suspended" ? "bg-red-50 text-red-500" : "bg-gray-100 text-gray-400"}`}>
                            {acct.status}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          {owed > 0 ? (
                            <button onClick={() => setRepaymentModal(acct)} className="px-3 py-1.5 rounded-lg bg-sky-50 text-sky-600 text-[12px] font-semibold hover:bg-sky-100 transition-all">Record Payment</button>
                          ) : <span className="text-gray-300 text-[12px]">Settled</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {accounts.length === 0 && (
                    <tr><td colSpan={7} className="px-5 py-10 text-center text-[13px] text-gray-400">No credit accounts yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══ REPAYMENTS TAB ══ */}
        {tab === "repayments" && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50">
              <h2 className="text-[14px] font-bold text-gray-800">All Credit Repayments</h2>
            </div>
            <div className="divide-y divide-gray-50">
              {repayments.map((rep) => (
                <div key={rep.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/40 transition-colors">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth={1.8} className="w-4 h-4"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-gray-800">{rep.tenant_name}</p>
                    <p className="text-[11.5px] text-gray-400">Meter {rep.meter_serial}{rep.mpesa_receipt ? ` · ${rep.mpesa_receipt}` : ""} · <span className="capitalize">{rep.method}</span></p>
                  </div>
                  <p className="text-[14px] font-bold text-emerald-600 flex-shrink-0">KES {fmt(parseFloat(rep.amount), 2)}</p>
                  <p className="text-[12px] text-gray-400 flex-shrink-0">{new Date(rep.created_at).toLocaleDateString("en-KE", { dateStyle: "medium" })}</p>
                </div>
              ))}
              {repayments.length === 0 && (
                <div className="px-5 py-10 text-center text-[13px] text-gray-400">No repayments recorded yet.</div>
              )}
            </div>
          </div>
        )}

        {/* ══ POLICIES TAB ══ */}
        {tab === "policies" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {policies.map((pol) => (
              <div key={pol.id} className={`bg-white rounded-2xl border shadow-sm p-5 ${pol.is_enabled ? "border-sky-100" : "border-gray-100"}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-[14px] font-bold text-gray-800">{pol.property_name}</p>
                    <span className={`inline-flex items-center gap-1.5 mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${pol.is_enabled ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-400"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${pol.is_enabled ? "bg-emerald-400" : "bg-gray-300"}`} />
                      {pol.is_enabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  <button onClick={() => setPolicyModal({ open: true, policy: pol })} className="text-[12px] text-sky-500 font-semibold hover:text-sky-600 transition-colors">Edit</button>
                </div>
                <div className="space-y-2">
                  {[
                    { label: "Credit Rate", value: `KES ${parseFloat(pol.credit_price_per_unit).toFixed(2)} / unit` },
                    { label: "Max Credit",  value: `KES ${fmt(parseFloat(pol.max_credit_amount))}` },
                    { label: "Grace Period", value: `${pol.grace_period_days} days` },
                    { label: "Late Fee",    value: parseFloat(pol.late_fee_percent) > 0 ? `${pol.late_fee_percent}%` : "None" },
                  ].map((d) => (
                    <div key={d.label} className="flex justify-between text-[12.5px]">
                      <span className="text-gray-400">{d.label}</span>
                      <span className="font-semibold text-gray-800">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {policies.length === 0 && (
              <div className="col-span-3 bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
                <p className="text-[14px] font-semibold text-gray-600">No credit policies yet</p>
                <p className="text-[12.5px] text-gray-400 mt-1">Create a policy to enable credit billing for a property.</p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* ── modals ── */}
      {policyModal.open && (
        <PolicyModal
          policy={policyModal.policy}
          properties={properties}
          onClose={() => setPolicyModal({ open: false, policy: null })}
          onSaved={load}
        />
      )}
      {reviewModal && (
        <ReviewModal req={reviewModal.req} action={reviewModal.action} onClose={() => setReviewModal(null)} onDone={load} />
      )}
      {repaymentModal && (
        <RepaymentModal account={repaymentModal} onClose={() => setRepaymentModal(null)} onDone={load} />
      )}
    </>
  );
}
